import React, {FC, useState, useEffect, useContext} from 'react';
import {useInformerConfigState} from '@/informers/services/informerConfigReducer';
import {useLocalizer} from '@/common/services/localizer';
import {ServicesContext} from '@/informers/contexts/ServicesContext';
import {FieldText} from '@/common/components/FieldText';
import {CodeResult} from '../CodeResult';
import styles from './FormComplete.css';

const validEmailRegexp = /^[-a-z0-9'_%+~#!$&*=]+(\.[-a-z0-9'_%+~#!$&*=]+)*@([a-z0-9][-a-z0-9]*\.)+[a-z]{2,20}$/i;

interface FormCompleteProps {
	onReset(): void;
}

export const FormComplete: FC<FormCompleteProps> = props => {
	const {onReset} = props;

	const t = useLocalizer();
	const {informersAPI} = useContext(ServicesContext)!;

	const {state, dispatch} = useInformerConfigState();
	const [emailError, setEmailError] = useState<'invalid' | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [codeShown, setCodeShown] = useState<null | {scriptUrl: string}>(null);

	const handleComplete = async (event: React.FormEvent) => {
		event.preventDefault();
		const email = state.email.trim();
		if (!validEmailRegexp.test(email) || email.split('@')[0].length > 64) {
			setEmailError('invalid');
			return;
		}

		setSubmitting(true);

		let response;
		try {
			response = await informersAPI.makeInformer(
				state.locations.map(item => item.id),
				email,
			);
		} finally {
			setSubmitting(false);
		}

		setCodeShown({scriptUrl: response.url});
	};

	const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		dispatch({
			type: 'setValues',
			values: {email: event.currentTarget.value},
		});
	};

	const handleReset = () => {
		dispatch({type: 'reset'});
		onReset();
	};

	const handleCodeHide = () => {
		setCodeShown(null);
	};

	// Reset en error on change
	useEffect(() => {
		if (emailError) {
			setEmailError(null);
		}
	}, [state.email]);

	return (
		<>
			<form onSubmit={handleComplete}>
				<label>
					<p className={styles.labelText}>{t('emailTitle')}</p>
					<FieldText
						className={styles.emailField}
						inputMode='email'
						value={state.email}
						maxLength={512}
						failed={emailError !== null}
						onChange={handleEmailChange}
					/>
					{emailError === 'invalid' && <p className={styles.error}>{t('incorrectEmail')}</p>}
				</label>
				<button className={styles.submitButton} type='submit' disabled={state.email === '' || submitting}>
					{t('complete')}
				</button>
				<button className={styles.resetButton} type='button' onClick={handleReset}>
					{t('reset')}
				</button>
			</form>

			{codeShown && <CodeResult scriptUrl={codeShown.scriptUrl} onClose={handleCodeHide} />}
		</>
	);
};

FormComplete.displayName = 'FormComplete';
